import Divider from 'antd/lib/divider';
import Space from 'antd/lib/space';
import * as React from 'react';
import dashboard from '../../../assets/dashboard-build-distro.svg';
import { AppComponentProps } from '../../typescriptHelpers';
import Text from 'antd/lib/typography/Text';
import { antGrey } from '../styles/globalStyles';
import { RocketOutlined } from '@ant-design/icons';
import { AppSettingsV2Updatable } from '../../actions/updatable';
import { getIsBuildDistroSupportedPlatform } from '../../helpers';
import { globalMessage } from '../../utils/globalMessage';
import { useDispatch, useSelector } from 'react-redux';
import { tryUpdateAppSettingsV2 } from '../../actions/appSettings';
import { RootState } from '../../reducers/reducers';
import { useIsMobile } from '../../mediaQueriesHelpers';
import { trackBuildDistroViewEvent } from '../../actions/trackEvent';
import * as AnalyticEvents from '../../utils/analytics';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { selectAppPlatform, selectUserCanPerformAction } from '../../selectors/selectors';
import { displayTextForAppPlatform } from '../../helpers/displayHelpers';
import { useError, useIsLoading } from '@/utils/hooks/helpers';

export type OptInScreenProps = AppComponentProps;

export const OptInScreen: React.FC<OptInScreenProps> = ({ appId, orgId }) => {
    const dispatch = useDispatch();

    const userCanPerformAction = useSelector((state: RootState) =>
        selectUserCanPerformAction(state, RunwayUserAction.UABuildDistroOptIn),
    );
    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));

    const isMobile = useIsMobile();

    const isEnablingBuildDistro = useIsLoading(AppSettingsV2Updatable.EnableBuildDistro);
    const enableBuildDistroError = useError(AppSettingsV2Updatable.EnableBuildDistro);

    const isBuildDistroSupportedPlatform = getIsBuildDistroSupportedPlatform(platform);

    React.useEffect(() => {
        dispatch(
            trackBuildDistroViewEvent({
                orgId,
                appId,
                eventName: AnalyticEvents.Event.BuildDistroViewOptInPage,
            }),
        );
    }, []);

    React.useEffect(() => {
        if (enableBuildDistroError) {
            globalMessage.error(enableBuildDistroError);
        }
    }, [enableBuildDistroError]);

    const enableBuildDistro = () => {
        dispatch(
            tryUpdateAppSettingsV2(AppSettingsV2Updatable.EnableBuildDistro, {
                orgId,
                appId,
            }),
        );
    };

    return (
        <div
            style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flex: 1,
            }}
        >
            <Space
                size={36}
                split={
                    <Divider
                        type="vertical"
                        style={{
                            height: 314,
                        }}
                    />
                }
            >
                <div
                    style={{
                        maxWidth: 318,
                        gap: 22,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'end',
                        textAlign: 'right',
                    }}
                >
                    <Text
                        style={{
                            color: antGrey.grey8,
                            fontSize: 24,
                            fontWeight: 600,
                        }}
                    >
                        {isBuildDistroSupportedPlatform ?
                            'Get started with Build Distro by Runway'
                        :   'Build Distro not supported'}
                    </Text>
                    <Text
                        style={{
                            fontSize: 20,
                            color: antGrey.grey7,
                        }}
                    >
                        {isBuildDistroSupportedPlatform ?
                            'Create buckets to group builds for testing. Upload builds, share them with your team, and install them easily without leaving Runway.'
                        :   `Build Distro is not supported for ${displayTextForAppPlatform(platform)} platforms.`}
                    </Text>
                    {isBuildDistroSupportedPlatform && (
                        <Space direction="vertical" size={14} align="end">
                            <PermissionedActionButton
                                type="primary"
                                icon={<RocketOutlined />}
                                loading={isEnablingBuildDistro}
                                onClick={() => enableBuildDistro()}
                                actionType={RunwayUserAction.UABuildDistroOptIn}
                            >
                                Get started
                            </PermissionedActionButton>
                            <Text
                                style={{
                                    color: antGrey.grey7,
                                    fontSize: 12,
                                }}
                            >
                                {userCanPerformAction ?
                                    `Note: continuing will opt you into build artifact storage in Runway`
                                :   `Contact an administrator to enable this Runway feature!`}
                            </Text>
                        </Space>
                    )}
                </div>
                {!isMobile && (
                    <img
                        src={dashboard}
                        width={336}
                        height={336}
                        style={{
                            filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))',
                        }}
                    />
                )}
            </Space>
        </div>
    );
};
